import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { AuthenticationService } from '../permission/authentication.service';
import { RouteAccessService } from '../permission/route-access.service';
import { LogService } from '../util/log.service';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard {
  constructor(
    private readonly router: Router,
    private readonly authService: AuthenticationService,
    private readonly routeAccessService: RouteAccessService,
    private readonly log: LogService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const requiredPermissions = route.data['requiredPermissions'] as string[];

    if (!requiredPermissions || requiredPermissions.length === 0) {
      this.log.debug('RoleGuard: No specific permissions required, access granted.');
      return true; // No specific permissions required
    }

    const userPermissions = this.authService.getUserPermissions();
    const userPermissionSet = new Set(userPermissions);

    this.log.debug(`RoleGuard: Checking for permissions: ${requiredPermissions.join(', ')}`);

    const hasAllRequiredPermissions = requiredPermissions.every((rp) => userPermissionSet.has(rp));

    if (hasAllRequiredPermissions) {
      this.log.debug('RoleGuard: User has all required permissions, access granted.');
      return true;
    }

    this.log.warn(
      `RoleGuard: User lacks required permissions. Needed: [${requiredPermissions.join(', ')}], Has: [${userPermissions.join(', ')}]. Access denied.`
    );

    // Special handling for the default route (root path or overview-st)
    const isRootPath = state.url === '/' || state.url === '';
    const isDefaultRoute = isRootPath || state.url === '/overview-st';

    if (isDefaultRoute) {
      // Check if we can find an alternative accessible route for the user
      const accessibleRoute = this.routeAccessService.getFirstAccessibleRoute();

      if (accessibleRoute) {
        this.log.info(`RoleGuard: User attempted to access ${state.url} but lacks permissions. Redirecting to accessible route: ${accessibleRoute}`);
        this.router.navigate([`/${accessibleRoute}`]);
        return false;
      }
    }

    // If not the root path or no accessible routes available, show access denied page
    this.router.navigate(['/auth/access'], { queryParams: { needed: requiredPermissions.join(',') } });
    return false;
  }
}
